import { computed, type Ref, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

type NavigableItem = { id: string }

export type UseContentItemNavigationOptions = {
  items: Ref<NavigableItem[]>
  totalPages: Ref<number>
  getPage: () => number
  setPage: (page: number) => void
}

/**
 * Shared prev/next navigation for the content library detail view.
 * Moves between items on the current page and, at a page boundary, fetches the
 * adjacent page (via setPage) and selects its first/last item once it loads.
 */
export const useContentItemNavigation = ({
  items,
  totalPages,
  getPage,
  setPage,
}: UseContentItemNavigationOptions) => {
  const route = useRoute()
  const router = useRouter()
  const pendingSelect = ref<'first' | 'last' | null>(null)

  const selectedId = computed(() => route.query.contentItemId as string | undefined)
  const currentPage = computed(() => getPage())
  const currentIndex = computed(
    () => items.value?.findIndex((item) => item.id === selectedId.value) ?? -1,
  )

  const hasPrevItem = computed(() => currentIndex.value > 0 || currentPage.value > 1)
  const hasNextItem = computed(() => {
    if (currentIndex.value >= 0 && currentIndex.value < (items.value?.length ?? 0) - 1) {
      return true
    }
    return currentPage.value < totalPages.value
  })

  const selectItem = (id: string) => {
    void router.push({ query: { ...route.query, contentItemId: id } })
  }

  const handleNavigate = (direction: 'prev' | 'next') => {
    const list = items.value ?? []
    const index = currentIndex.value
    if (direction === 'next') {
      if (index >= 0 && index < list.length - 1) {
        selectItem(list[index + 1]!.id)
      } else if (currentPage.value < totalPages.value) {
        pendingSelect.value = 'first'
        setPage(currentPage.value + 1)
      }
      return
    }
    if (index > 0) {
      selectItem(list[index - 1]!.id)
    } else if (currentPage.value > 1) {
      pendingSelect.value = 'last'
      setPage(currentPage.value - 1)
    }
  }

  watch(items, (list) => {
    if (!pendingSelect.value || !list || list.length === 0) {
      return
    }
    const target = pendingSelect.value === 'first' ? list[0] : list[list.length - 1]
    pendingSelect.value = null
    if (target) {
      selectItem(target.id)
    }
  })

  return { hasPrevItem, hasNextItem, handleNavigate }
}
